import { Fragment, useCallback, useEffect, useState } from 'react';
import { ChevronRight, Crown, Loader2, Target } from 'lucide-react';
import { trainingApi } from '../../services/api';
import { useAppStore } from '../../store/useAppStore';

/**
 * The Programme map (EP-26 FE-17 ST-5): what the programme is for and when it is done,
 * how its work is bundled, the stages of its process, and -- per campaign -- where each
 * batch and round stands in those stages and what it waits on. Every row and cell opens
 * the work item behind it.
 */
type D = Record<string, any>;

const CELL: Record<string, string> = {
  done: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  active: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  todo: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  none: '',
};

export default function ProgrammeMap({ programId }: { programId: string }) {
  const [m, setM] = useState<D | null>(null);
  const [error, setError] = useState('');
  const open = useAppStore((s) => s.setDetailItemId);
  const load = useCallback(() => {
    trainingApi.programmeMap(programId).then(setM).catch((e) => setError(String(e?.message || e)));
  }, [programId]);
  useEffect(() => { setM(null); load(); }, [load]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!m) return <p className="text-sm text-gray-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Reading the programme map…</p>;
  const o = m.objective;
  const batchStages: D[] = m.stages.batch || [];
  const roundStages: D[] = m.stages.round || [];
  const knowledge: D[] = m.stages.knowledge || [];

  return (
    <div className="space-y-4">
      {/* 1. What it is for, and when it is done */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-semibold flex items-center gap-1"><Target className="w-4 h-4 text-accent" /> Objective</h2>
        <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">{o.statement || <span className="text-gray-400">No objective stated yet: add one under Settings › Profile (objectives).</span>}</p>
        {o.mvp && <p className="mt-1 text-sm"><span className="font-medium">MVP:</span> {o.mvp}</p>}
        <div className="mt-2 grid gap-3 md:grid-cols-[1fr,16rem]">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Done when{o.stated ? '' : ' (from the pass marks)'}</div>
            <ul className="mt-1 list-disc pl-5 text-sm text-gray-700 dark:text-gray-300">{o.doneWhen.map((d: string) => <li key={d}>{d}</li>)}</ul>
          </div>
          <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-2">
            <div className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> Champion</div>
            <div className="text-sm mt-0.5">{o.champion || <span className="text-gray-400">none yet: the first round to pass and be promoted becomes it</span>}</div>
          </div>
        </div>
      </section>

      {/* 2. How the work is bundled */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-semibold">How the work is bundled</h2>
        <div className="mt-2 flex flex-wrap items-stretch gap-1">
          {(m.bundling as D[]).map((b, i) => (
            <Fragment key={b.level}>
              {i > 0 && <ChevronRight className="w-4 h-4 text-gray-400 self-center" />}
              <div className="rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-1 max-w-[13rem]" title={b.means}>
                <div className="text-sm font-medium">{b.level}</div>
                <div className="text-[11px] text-gray-500 leading-tight">{b.here ? `${b.here}: ` : ''}{b.means}</div>
              </div>
            </Fragment>))}
        </div>
        {knowledge.length > 0 && (
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
            Before any batch: <strong>{knowledge.map((k) => k.title).join(' → ')}</strong> (done once for the programme, steps {knowledge[0].step}–{knowledge[knowledge.length - 1].step}).
            Then each batch goes through the data steps and each round through the build steps below.
          </p>)}
      </section>

      <div className="flex justify-end">
        <a href={`/training/${encodeURIComponent(programId)}/campaigns/new`} className="px-3 py-1.5 text-sm rounded bg-accent text-white">Plan a campaign</a>
      </div>

      <BatchPriority programId={programId} open={open} />
      <RowBudget programId={programId} atoms={(m.campaigns as D[]).flatMap((c) => c.rows).filter((r: D) => r.track === 'batch').reduce((n: number, r: D) => n + (r.units || 0), 0)} />

      {/* 3. Where each bundle stands */}
      {(m.campaigns as D[]).map((c) => (
        <section key={c.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 overflow-x-auto">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <button type="button" onClick={() => open(c.id)} className="text-sm font-semibold hover:text-accent">
              <span className="font-mono text-[11px] text-gray-400 mr-1">{c.key}</span>{c.title}
            </button>
            {c.stream && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{c.stream} stream</span>}
            <span className="text-xs text-gray-500">{c.status}</span>
          </div>
          <table className="text-xs border-separate border-spacing-y-1 min-w-full">
            <thead>
              <tr className="text-gray-500">
                <th className="text-left font-medium pr-3 w-72">Batch / round</th>
                <th colSpan={batchStages.length} className="text-center font-medium border-b border-gray-200 dark:border-gray-700">Produce the training data (per batch)</th>
                <th className="w-2" />
                <th colSpan={roundStages.length} className="text-center font-medium border-b border-gray-200 dark:border-gray-700">Build and release (per round)</th>
              </tr>
              <tr className="text-gray-500">
                <th />
                {batchStages.map((s) => <th key={s.id} className="px-1 font-normal max-w-[6rem]" title={`Step ${s.step}`}>{s.title}</th>)}
                <th />
                {roundStages.map((s) => <th key={s.id} className="px-1 font-normal max-w-[6rem]" title={`Step ${s.step}`}>{s.title}</th>)}
              </tr>
            </thead>
            <tbody>
              {(c.rows as D[]).map((r) => (
                <tr key={r.id} className="align-top">
                  <td className="pr-3">
                    <button type="button" onClick={() => open(r.id)} className="text-left hover:text-accent">
                      <span className="font-mono text-[10px] text-gray-400 mr-1">{r.key}</span>
                      <span className="text-gray-900 dark:text-gray-100">{r.title}</span>
                    </button>
                    <div className="text-[11px] text-gray-500">
                      {r.statusLabel}{r.track === 'batch' && r.units ? ` · ${r.units} units` : ''}
                      {r.track === 'round' && r.atomPin !== 'n/a' ? ` · atom pin: ${r.atomPin || 'not set'}` : ''}
                    </div>
                    {r.waiting && (
                      <div className="text-[11px] text-amber-700 dark:text-amber-400">
                        next: {r.waiting.toLabel || r.waiting.to} — {r.waiting.reason}
                      </div>)}
                  </td>
                  {(r.track === 'batch' ? r.cells : batchStages.map(() => null)).map((cell: D | null, i: number) => <Cell key={`b${i}`} cell={cell} open={open} />)}
                  <td />
                  {(r.track === 'round' ? r.cells : roundStages.map(() => null)).map((cell: D | null, i: number) => <Cell key={`r${i}`} cell={cell} open={open} />)}
                </tr>))}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-gray-500">
            Each cell is the steps of that stage for that batch or round: done of total. Blue = being worked, green = done, grey = not started.
            Click a cell to open its next open step.
          </p>
        </section>))}
      {!(m.campaigns as D[]).length && <p className="text-sm text-gray-500">No campaigns yet.</p>}
    </div>
  );
}

function Cell({ cell, open }: { cell: D | null; open: (id: string) => void }) {
  if (!cell || cell.state === 'none') return <td className="px-1 text-center text-gray-300">·</td>;
  return (
    <td className="px-1">
      <button type="button" disabled={!cell.next} onClick={() => cell.next && open(cell.next._id)}
        title={`${cell.done}/${cell.total} steps done${cell.holders.length ? ` · worked by ${cell.holders.join(', ')}` : ''}${cell.manual ? ` · ${cell.manual} manual` : ''}${cell.next ? ` · next: ${cell.next.title}` : ''}`}
        className={`w-full rounded px-1 py-0.5 text-center ${CELL[cell.state]}`}>
        {cell.done}/{cell.total}{cell.holders.length ? ' ●' : ''}
      </button>
    </td>
  );
}

/** Batches ranked by WSJF computed from their data (EP-26 FE-14 ST-7; A251 4.6): each term with
 *  what it was read from. The dispatcher recomputes daily; Recompute writes it now. */
function BatchPriority({ programId, open }: { programId: string; open: (id: string) => void }) {
  const [w, setW] = useState<D | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => { trainingApi.wsjf(programId).then(setW).catch((e) => setError(String(e?.message || e))); }, [programId]);
  useEffect(() => { load(); }, [load]);
  const recompute = async () => {
    setBusy(true); setError('');
    try { setW(await trainingApi.computeWsjf(programId)); } catch (e: any) { setError(e?.message || 'Not computed.'); }
    setBusy(false);
  };
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">Batch priority (WSJF computed from the data)</h2>
        <button type="button" disabled={busy} onClick={recompute} className="ml-auto text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50">
          {busy ? 'Computing…' : 'Recompute'}</button>
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      {!w ? <p className="text-xs text-gray-500 mt-1">Reading…</p> : w.state !== 'ok' ? <p className="text-xs text-amber-700 mt-1">{w.message}</p> : (
        <table className="mt-2 w-full text-xs">
          <thead><tr className="text-left text-gray-500"><th>#</th><th>Batch</th><th title="apps its atoms serve">BV</th><th title="earliest due date of an open item that depends on it">TC</th>
            <th title="shared atoms, failed eval layers, declined-case gaps">RR</th><th title="grades short and atoms needing a generator">Job</th><th>WSJF</th><th>Why</th></tr></thead>
          <tbody>
            {(w.ranking as D[]).map((x) => (
              <tr key={x._id} className="border-t border-gray-100 dark:border-gray-700 align-top">
                <td className="py-0.5">{x.rank}</td>
                <td><button type="button" onClick={() => open(x._id)} className="text-left hover:text-accent"><span className="font-mono text-[10px] text-gray-400 mr-1">{x.key}</span>{x.title}</button></td>
                <td>{x.businessValue}</td><td>{x.timeCriticality}</td><td>{x.riskReductionOpportunity}</td><td>{x.jobSize}</td>
                <td className="font-semibold">{x.score}</td>
                <td className="text-[11px] text-gray-600 dark:text-gray-400">
                  {x.inputs.apps.length ? `serves ${x.inputs.apps.join(', ')}` : 'serves no app in the ontology'}
                  {x.inputs.dueInDays != null ? ` · due in ${x.inputs.dueInDays} d` : ' · no dated dependant'}
                  {` · ${x.inputs.drivers.sharedAtoms} shared, ${x.inputs.drivers.failedEvalLayers} in failed eval layers, ${x.inputs.drivers.declinedCaseGaps} short of declined cases`}
                  {` · ${x.inputs.gradesShort} grades short, ${x.inputs.noEvidenceType} need a generator`}
                </td>
              </tr>))}
            {(w.refused as D[]).map((x) => (
              <tr key={x._id} className="border-t border-gray-100 dark:border-gray-700"><td>–</td>
                <td><span className="font-mono text-[10px] text-gray-400 mr-1">{x.key}</span>{x.title}</td>
                <td colSpan={6} className="text-amber-700">{x.reason}</td></tr>))}
          </tbody>
        </table>)}
      {w?.state === 'ok' && <p className="mt-1 text-[11px] text-gray-500">{w.version}; eval verdict {w.evalVerdict || 'none yet'}. The terms are written onto each batch, so the WSJF, Kanban and Gantt views rank by them.</p>}
    </section>
  );
}

/** The row budget for the next training set (A251 4.4): measured rows per GPU-hour on a base model
 *  and GPU, the planned hours, and how many atoms that allows at the depth floor. Read only. */
function RowBudget({ programId, atoms }: { programId: string; atoms: number }) {
  const [pairs, setPairs] = useState<D[] | null>(null);
  const [pick, setPick] = useState('');
  const [hours, setHours] = useState(4);
  const [b, setB] = useState<D | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    trainingApi.rowBudget(programId).then((r) => { setPairs(r.pairs || []); if (r.pairs?.length) setPick(`${r.pairs[0].baseModel}|${r.pairs[0].gpu}`); })
      .catch((e) => setError(String(e?.message || e)));
  }, [programId]);
  useEffect(() => {
    if (!pick) return;
    const [baseModel, gpu] = pick.split('|');
    trainingApi.rowBudget(programId, { baseModel, gpu, hours, atoms }).then(setB).catch((e) => setError(String(e?.message || e)));
  }, [programId, pick, hours, atoms]);
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h2 className="text-sm font-semibold">Row budget for the next training set</h2>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      {pairs && !pairs.length && <p className="text-xs text-amber-700 mt-1">No finished training run recorded what it consumed yet. Request a calibration run (stage train, calibration true, at most 1 hour, on the A4500).</p>}
      {pairs && pairs.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600">
            {pairs.map((p) => <option key={`${p.baseModel}|${p.gpu}`} value={`${p.baseModel}|${p.gpu}`}>{p.baseModel} on {p.gpu} ({p.rowsPerGpuHour} rows/GPU-h)</option>)}
          </select>
          <label className="flex items-center gap-1">GPU-hours <input type="number" min={0.5} step={0.5} value={hours} onChange={(e) => setHours(Number(e.target.value) || 4)} className="w-16 px-1 py-0.5 border rounded dark:bg-gray-700 dark:border-gray-600" /></label>
        </div>)}
      {b?.state === 'ok' && (
        <p className="mt-2 text-sm">
          {b.totalRows.toLocaleString()} rows consumed in {b.hours} GPU-h = <strong>{b.uniqueRows.toLocaleString()} unique rows</strong> at {b.repetition}x repetition;
          up to <strong>{b.atomCap} atoms</strong> at {b.floor} rows each.
          {b.atoms ? (b.fits ? ` The ${b.atoms} atoms in the batches fit, at ${b.rowsPerAtom} rows per atom.` : ` ${b.why}`) : ''}
          <span className="block text-[11px] text-gray-500">Measured on {b.reference.run} ({b.reference.source}{b.reference.calibration ? ', calibration run' : ''}).</span>
        </p>)}
      {b?.state === 'needs_calibration' && <p className="mt-2 text-xs text-amber-700">{b.message}</p>}
    </section>
  );
}
